import { z } from 'zod';
import { IsoDateTime, UuidString } from '../../primitives.js';

/**
 * FieldDefinition — определение custom-поля per Project (или per Workspace
 * через project_id=null). Динамическая Zod-схема для validate(custom_fields)
 * собирается из массива FieldDefinition в engine `custom-fields-validator.ts`
 * (BE-S12.1-12).
 *
 * 13 типов покрывают ERP-кейсы:
 *   - decimal с UoM-привязкой (для объёма/массы)
 *   - money с currency (для AR/AP контекста)
 *   - entity_ref — ссылка на любую ERP-сущность (auto-rendering chip как subject)
 *   - rich_text — TipTap pass-through
 *
 * См. `docs/STAITER_UX.md` §11.21 и `docs/backlogs/task-module/be-backlog.md` BE-S12.1-6.
 */

export const FieldType = z.enum([
  'text',
  'rich_text',
  'number',
  'decimal',
  'money',
  'date',
  'datetime',
  'single_select',
  'multi_select',
  'user_picker',
  'entity_ref',
  'checkbox',
  'url',
]);
export type FieldType = z.infer<typeof FieldType>;

export const FieldDefinition = z.object({
  id: UuidString,
  entity_id: UuidString,
  /** Null = workspace-shared template (через FieldTemplate). */
  project_id: UuidString.nullable(),
  /** Snake-case, ≤40 символов. Используется как JSONB-ключ. */
  key: z
    .string()
    .min(1)
    .max(40)
    .regex(/^[a-z][a-z0-9_]*$/, 'key должен быть snake_case'),
  label: z.string().min(1).max(100),
  type: FieldType,
  required: z.boolean(),
  /** Default — type-specific. Не парсится здесь; validate в custom-fields-validator engine. */
  default_value: z.unknown().nullable(),
  /** Per-type constraints (options для select, uom_group для decimal, и т.п.). */
  constraints: z.record(z.string(), z.unknown()),
  /** Если attached из workspace template — ссылка на исходник. */
  template_id: UuidString.nullable(),
  sequence: z.number().finite(),
  /** Soft-delete (30d retention; rename key мигрирует JSONB в background). */
  soft_deleted_at: IsoDateTime.nullable(),
  created_at: IsoDateTime,
  updated_at: IsoDateTime,
});
export type FieldDefinition = z.infer<typeof FieldDefinition>;
